import { useEffect, useRef, useState } from "react";

/**
 * LuxeCursor — cursor personalizado premium (solo escritorio con hover:fine).
 * Un punto sólido + un anillo que sigue con leve retardo (RAF lerp).
 * Se agranda al pasar sobre elementos interactivos. Se oculta en móvil
 * y cuando `prefers-reduced-motion: reduce` está activo.
 */
export default function LuxeCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const mqHover = window.matchMedia("(hover: hover) and (pointer: fine)");
    const mqRM = window.matchMedia("(prefers-reduced-motion: reduce)");
    const ok = mqHover.matches && !mqRM.matches;
    setEnabled(ok);
    if (ok) {
      document.documentElement.setAttribute("data-cursor", "luxe");
    }
    return () => document.documentElement.removeAttribute("data-cursor");
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let rx = mx;
    let ry = my;
    let raf = 0;

    const move = (e) => {
      mx = e.clientX;
      my = e.clientY;
      dot.style.transform = `translate3d(${mx}px, ${my}px, 0) translate(-50%, -50%)`;
    };
    const loop = () => {
      // Lerp del anillo (retardo suave)
      rx += (mx - rx) * 0.18;
      ry += (my - ry) * 0.18;
      ring.style.transform = `translate3d(${rx}px, ${ry}px, 0) translate(-50%, -50%)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.addEventListener("pointermove", move, { passive: true });

    // Interactive elements → agrandar cursor
    const interactive = "a, button, [role='button'], input, textarea, select, .card-lift, .tactile-card";
    const onEnter = () => {
      dot.dataset.active = "1";
      ring.dataset.active = "1";
    };
    const onLeave = () => {
      dot.dataset.active = "0";
      ring.dataset.active = "0";
    };
    const attach = () => {
      document.querySelectorAll(interactive).forEach((el) => {
        el.addEventListener("pointerenter", onEnter);
        el.addEventListener("pointerleave", onLeave);
      });
    };
    // Retarda para asegurar que DOM esté listo
    const t = window.setTimeout(attach, 400);
    // Re-attach on DOM changes (delegated but simple)
    const obs = new MutationObserver(() => {
      attach();
    });
    obs.observe(document.body, { childList: true, subtree: true });

    // Oculta al salir de la ventana
    const onOut = () => {
      dot.style.opacity = "0";
      ring.style.opacity = "0";
    };
    const onIn = () => {
      dot.style.opacity = "1";
      ring.style.opacity = "1";
    };
    document.addEventListener("mouseleave", onOut);
    document.addEventListener("mouseenter", onIn);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", move);
      document.removeEventListener("mouseleave", onOut);
      document.removeEventListener("mouseenter", onIn);
      window.clearTimeout(t);
      obs.disconnect();
      document.querySelectorAll(interactive).forEach((el) => {
        el.removeEventListener("pointerenter", onEnter);
        el.removeEventListener("pointerleave", onLeave);
      });
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <>
      <div ref={ringRef} className="luxe-cursor__ring" data-testid="luxe-cursor-ring" aria-hidden="true" />
      <div ref={dotRef} className="luxe-cursor" data-testid="luxe-cursor" aria-hidden="true" />
    </>
  );
}
